/**
 * Health Context Service
 *
 * Aggregates patient health data from existing hooks/data sources into a unified
 * PatientHealthContext for the AI Health Companion.
 */

import {
  PatientHealthContext,
  VitalMeasurement,
  HealthTrend,
  MedicationInfo,
  RecentTestSummary,
  UpcomingAppointmentInfo,
  ScreeningDue,
  PatientDemographics,
} from '../types/ai-health-companion-types';

/**
 * Configuration for vital trend analysis
 */
interface TrendConfig {
  /** Threshold for determining significant change (default: 0.05 or 5% change) */
  threshold?: number;
  /** Whether lower values are better (e.g., blood pressure, glucose) */
  lowerIsBetter?: boolean;
}

/**
 * Simulates async data fetching delay
 */
const simulateDelay = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

/**
 * Assembles a complete patient health context with realistic mock data
 *
 * @param userId - The patient's user ID
 * @returns Complete patient health context
 */
export async function assembleHealthContext(_userId: string): Promise<PatientHealthContext> {
  // Simulate API delay
  await simulateDelay(300);

  const currentDate = new Date('2026-02-14');

  // Create realistic vitals with proper measurements
  const vitals = {
    latest: {
      heartRate: {
        value: 72,
        unit: 'bpm',
        date: currentDate.toISOString(),
        status: 'normal',
      } as VitalMeasurement,
      bloodPressureSystolic: {
        value: 128,
        unit: 'mmHg',
        date: currentDate.toISOString(),
        status: 'normal',
      } as VitalMeasurement,
      bloodPressureDiastolic: {
        value: 82,
        unit: 'mmHg',
        date: currentDate.toISOString(),
        status: 'normal',
      } as VitalMeasurement,
      temperature: {
        value: 98.6,
        unit: '°F',
        date: currentDate.toISOString(),
        status: 'normal',
      } as VitalMeasurement,
      oxygenSaturation: {
        value: 98,
        unit: '%',
        date: currentDate.toISOString(),
        status: 'normal',
      } as VitalMeasurement,
      respiratoryRate: {
        value: 16,
        unit: '/min',
        date: currentDate.toISOString(),
        status: 'normal',
      } as VitalMeasurement,
    },
    trends: {
      heartRate: 'stable' as HealthTrend,
      bloodPressureSystolic: 'improving' as HealthTrend,
      bloodPressureDiastolic: 'stable' as HealthTrend,
      temperature: 'stable' as HealthTrend,
      oxygenSaturation: 'stable' as HealthTrend,
      respiratoryRate: 'stable' as HealthTrend,
    },
  };

  // Current medications
  const medications: MedicationInfo[] = [
    {
      name: 'Lisinopril',
      dosage: '10mg',
      frequency: 'daily',
      status: 'active',
    },
    {
      name: 'Metformin',
      dosage: '500mg',
      frequency: 'twice daily',
      status: 'active',
    },
    {
      name: 'Atorvastatin',
      dosage: '20mg',
      frequency: 'daily',
      status: 'active',
    },
    {
      name: 'Vitamin D',
      dosage: '2000IU',
      frequency: 'daily',
      status: 'active',
    },
  ];

  // Medical conditions
  const conditions = [
    'Type 2 Diabetes',
    'Hypertension',
    'Hyperlipidemia',
  ];

  // Known allergies
  const allergies = [
    'Penicillin',
    'Sulfa drugs',
  ];

  // Recent test results
  const recentTests: RecentTestSummary[] = [
    {
      name: 'Complete Blood Count (CBC)',
      date: new Date(currentDate.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days ago
      status: 'completed',
      flagged: false,
    },
    {
      name: 'Hemoglobin A1C',
      date: new Date(currentDate.getTime() - 14 * 24 * 60 * 60 * 1000).toISOString(), // 14 days ago
      status: 'completed',
      flagged: true,
    },
    {
      name: 'Lipid Panel',
      date: new Date(currentDate.getTime() - 21 * 24 * 60 * 60 * 1000).toISOString(), // 21 days ago
      status: 'completed',
      flagged: false,
    },
  ];

  // Upcoming appointments
  const upcomingAppointments: UpcomingAppointmentInfo[] = [
    {
      date: new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
      type: 'Cardiology Follow-up',
      provider: 'Dr. Sarah Chen',
    },
    {
      date: new Date(currentDate.getTime() + 21 * 24 * 60 * 60 * 1000).toISOString(), // 21 days from now
      type: 'Endocrinology Consultation',
      provider: 'Dr. Michael Rodriguez',
    },
  ];

  // Demographics
  const demographics: PatientDemographics = {
    age: 45,
    sex: 'male',
  };

  // Get age and sex-appropriate screenings
  const screeningsDue = getScreeningsDue(demographics.age, demographics.sex);

  return {
    vitals,
    medications,
    conditions,
    allergies,
    recentTests,
    upcomingAppointments,
    screeningsDue,
    demographics,
  };
}

/**
 * Computes health trends from historical data points using linear regression
 *
 * @param dataPoints - Array of timestamped measurements
 * @param periodDays - Number of days to analyze (default: 14)
 * @returns Health trend classification
 */
export function computeVitalTrends(
  dataPoints: { date: string; value: number }[],
  periodDays: number = 14,
  config: TrendConfig = {}
): HealthTrend {
  const { threshold = 0.05, lowerIsBetter = true } = config;

  // Need at least 3 data points for meaningful analysis
  if (dataPoints.length < 3) {
    return 'unknown';
  }

  // Filter data points within the specified period
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - periodDays);

  const recentPoints = dataPoints
    .filter(point => new Date(point.date) >= cutoffDate)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  if (recentPoints.length < 3) {
    return 'unknown';
  }

  // Simple linear regression
  const n = recentPoints.length;
  const startTime = new Date(recentPoints[0].date).getTime();

  // Convert dates to days since start for x-axis
  const xValues = recentPoints.map(p =>
    (new Date(p.date).getTime() - startTime) / (1000 * 60 * 60 * 24)
  );
  const yValues = recentPoints.map(p => p.value);

  // Calculate means
  const xMean = xValues.reduce((sum, x) => sum + x, 0) / n;
  const yMean = yValues.reduce((sum, y) => sum + y, 0) / n;

  // Calculate slope (beta)
  let numerator = 0;
  let denominator = 0;

  for (let i = 0; i < n; i++) {
    numerator += (xValues[i] - xMean) * (yValues[i] - yMean);
    denominator += Math.pow(xValues[i] - xMean, 2);
  }

  const slope = denominator !== 0 ? numerator / denominator : 0;

  // Normalize slope by mean value to get percentage change per day
  const normalizedSlope = yMean !== 0 ? slope / yMean : 0;

  // Calculate daily rate of change as percentage
  const changeRate = Math.abs(normalizedSlope);

  // Determine trend based on slope and configuration
  if (changeRate < threshold) {
    return 'stable';
  }

  // For metrics where lower is better (BP, glucose, weight)
  if (lowerIsBetter) {
    return slope < 0 ? 'improving' : 'worsening';
  } else {
    // For metrics where higher is better (oxygen saturation)
    return slope > 0 ? 'improving' : 'worsening';
  }
}

/**
 * Returns age and sex-appropriate preventive screening recommendations
 *
 * @param age - Patient's age in years
 * @param sex - Patient's biological sex
 * @returns Array of due or overdue screenings
 */
export function getScreeningsDue(age: number, sex: string): ScreeningDue[] {
  const currentDate = new Date('2026-02-14');
  const screenings: ScreeningDue[] = [];

  // Annual physical exam (everyone)
  screenings.push({
    name: 'Annual Physical Examination',
    dueDate: new Date(currentDate.getTime() + 45 * 24 * 60 * 60 * 1000).toISOString(), // 45 days from now
    overdue: false,
  });

  // Cholesterol screening (adults 40+, every 5 years)
  if (age >= 40) {
    screenings.push({
      name: 'Cholesterol Screening',
      dueDate: new Date(currentDate.getTime() + 180 * 24 * 60 * 60 * 1000).toISOString(), // 6 months from now
      overdue: false,
    });
  }

  // Colon cancer screening (adults 45+, every 10 years)
  if (age >= 45) {
    screenings.push({
      name: 'Colonoscopy',
      dueDate: new Date(currentDate.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString(), // 90 days overdue
      overdue: true,
    });
  }

  // Mammogram (females 40+, every 2 years)
  if (sex.toLowerCase() === 'female' && age >= 40) {
    screenings.push({
      name: 'Mammogram',
      dueDate: new Date(currentDate.getTime() + 60 * 24 * 60 * 60 * 1000).toISOString(), // 2 months from now
      overdue: false,
    });
  }

  // Prostate screening (males 50+, every 2 years)
  if (sex.toLowerCase() === 'male' && age >= 50) {
    screenings.push({
      name: 'Prostate Cancer Screening (PSA)',
      dueDate: new Date(currentDate.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString(), // 3 months from now
      overdue: false,
    });
  }

  // Diabetes screening (adults 45+, every 3 years)
  if (age >= 45) {
    screenings.push({
      name: 'Diabetes Screening (Fasting Glucose)',
      dueDate: new Date(currentDate.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days overdue
      overdue: true,
    });
  }

  // Eye exam for diabetics (annually)
  // Note: This assumes the patient has diabetes based on the mock data
  // In a real implementation, this would check the conditions array
  if (age >= 18) {
    screenings.push({
      name: 'Diabetic Eye Exam',
      dueDate: new Date(currentDate.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 1 month from now
      overdue: false,
    });
  }

  return screenings;
}

/**
 * Formats the PatientHealthContext into a concise text summary for AI prompts
 *
 * @param context - Complete patient health context
 * @returns Formatted text summary
 */
export function summarizeForAI(context: PatientHealthContext): string {
  const { demographics, conditions, allergies, medications, vitals, recentTests, upcomingAppointments, screeningsDue } = context;

  // Patient demographics
  const patientInfo = `Patient: ${demographics.age}yo ${demographics.sex}`;

  // Medical conditions
  const conditionsText = conditions.length > 0
    ? `Conditions: ${conditions.join(', ')}`
    : 'Conditions: None documented';

  // Allergies
  const allergiesText = allergies.length > 0
    ? `Allergies: ${allergies.join(', ')}`
    : 'Allergies: No known allergies';

  // Current medications
  const medsText = medications.length > 0
    ? `Current Medications: ${medications.map(m => `${m.name} ${m.dosage} ${m.frequency}`).join(', ')}`
    : 'Current Medications: None';

  // Latest vitals with trends
  const vitalsEntries = Object.entries(vitals.latest);
  const vitalsText = vitalsEntries.length > 0
    ? `Latest Vitals: ${vitalsEntries.map(([key, vital]) => {
        const trend = vitals.trends[key] || 'unknown';
        const trendIndicator = trend !== 'stable' && trend !== 'unknown' ? ` (${trend})` : '';

        // Format vital name
        const vitalName = key
          .replace(/([A-Z])/g, ' $1')
          .trim()
          .replace(/^./, str => str.toUpperCase());

        return `${vitalName} ${vital.value}${vital.unit}${trendIndicator}`;
      }).join(', ')}`
    : 'Latest Vitals: Not available';

  // Recent tests
  const testsText = recentTests.length > 0
    ? `Recent Tests: ${recentTests.map(t => {
        const flagged = t.flagged ? ' (flagged)' : '';
        const daysAgo = Math.floor(
          (new Date('2026-02-14').getTime() - new Date(t.date).getTime()) / (1000 * 60 * 60 * 24)
        );
        return `${t.name} (${daysAgo}d ago, ${t.status}${flagged})`;
      }).join(', ')}`
    : 'Recent Tests: None';

  // Upcoming appointments
  const appointmentsText = upcomingAppointments.length > 0
    ? `Upcoming: ${upcomingAppointments.map(apt => {
        const date = new Date(apt.date);
        const daysUntil = Math.floor(
          (date.getTime() - new Date('2026-02-14').getTime()) / (1000 * 60 * 60 * 24)
        );
        return `${apt.type} with ${apt.provider} (in ${daysUntil}d)`;
      }).join(', ')}`
    : 'Upcoming: No scheduled appointments';

  // Overdue screenings
  const overdueScreenings = screeningsDue.filter(s => s.overdue);
  const screeningsText = overdueScreenings.length > 0
    ? `Screenings Due: ${overdueScreenings.map(s => {
        const overdueDate = new Date(s.dueDate);
        const daysOverdue = Math.floor(
          (new Date('2026-02-14').getTime() - overdueDate.getTime()) / (1000 * 60 * 60 * 24)
        );
        return `${s.name} (overdue by ${daysOverdue}d)`;
      }).join(', ')}`
    : '';

  // Combine all sections
  const sections = [
    patientInfo,
    conditionsText,
    allergiesText,
    medsText,
    vitalsText,
    testsText,
    appointmentsText,
    screeningsText,
  ].filter(Boolean); // Remove empty strings

  return sections.join('. ') + '.';
}
